import { Injectable } from '@angular/core';
import { ItemResponseModelV2, SupplierV2 } from './models/item-response-model-v2';

@Injectable({
    providedIn: 'root'
})
export class ItemStateValidationService {

    /**
     * Validates if an item can be set to ACTIVE state based on the required criteria:
     * - At least one supplier that meets ALL of the following:
     *   * StockCode is not null/empty and not "NA"
     *   * CountryOfOriginCode has a value
     *   * NetWeight > 0 (unless StockCode is "RI")
     * - AlsisProductCode not null or empty on the item
     *
     * Merges incoming suppliers with existing suppliers (incoming takes priority,
     * existing non-duplicates are appended), mirroring the backend merge logic.
     *
     * @param existingItem The existing item loaded from the API
     * @param incomingSuppliers Optional incoming supplier overrides (keyed by supplierId+itemId)
     * @returns Whether the item can be set to ACTIVE state
     */
    public canItemStateBeSetToActive(
        existingItem: ItemResponseModelV2,
        incomingSuppliers?: SupplierV2[]
    ): boolean {
        // AlsisProductCode must be present on the item
        if (!existingItem?.itemDetails?.alsisProductCode?.trim()) {
            return false;
        }

        // Build merged supplier list: incoming first, then existing non-duplicates
        const allSuppliers: SupplierV2[] = [];

        if (incomingSuppliers?.length) {
            allSuppliers.push(...incomingSuppliers);
        }

        if (existingItem.suppliers?.length) {
            for (const existingSupplier of existingItem.suppliers) {
                const alreadyExists = allSuppliers.some(s =>
                    s.supplierDetails.supplierId.toLowerCase() ===
                        existingSupplier.supplierDetails.supplierId.toLowerCase()
                );
                if (!alreadyExists) {
                    allSuppliers.push(existingSupplier);
                }
            }
        }

        if (allSuppliers.length === 0) {
            return false;
        }

        return allSuppliers.some(supplier => this.isValidSupplierForActive(supplier));
    }

    private isValidSupplierForActive(supplier: SupplierV2): boolean {
        const stockCode = supplier.supplierDetails?.stockCode;

        // StockCode must not be null/empty or "NA"
        if (!stockCode || stockCode.toUpperCase() === 'NA') {
            return false;
        }

        // CountryOfOriginCode must have a value
        if (!supplier.supplierDetails?.countryOfOriginCode?.trim()) {
            return false;
        }

        // If StockCode is "RI", NetWeight is not required
        if (stockCode.toUpperCase() === 'RI') {
            return true;
        }

        // All other StockCode values require NetWeight > 0
        const netWeight = supplier.dimensions?.netWeight;
        return netWeight != null && netWeight > 0;
    }
}
